import { Star, BarChart3, Home } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

interface DiamondIconProps {
  icon: React.ElementType;
  colorClass?: string;
}

function DiamondIcon({ icon: Icon, colorClass = 'text-primary' }: DiamondIconProps) {
  return (
    <div className="relative flex h-16 w-16 items-center justify-center group">
      {/* Diamond shape (rotated square) */}
      <div className="absolute inset-0 rotate-45 rounded-md bg-secondary transition-all duration-300 group-hover:bg-primary/10 group-hover:scale-110" />
      <Icon className={cn('relative z-10 h-6 w-6 transition-transform duration-300 group-hover:scale-110', colorClass)} />
    </div>
  );
}

const diferenciais = [
  {
    icon: Star,
    colorClass: 'text-primary',
    title: 'Atendimento transparente',
    description: 'Você entende cada etapa com clareza e sem pressão.',
  },
  {
    icon: BarChart3,
    colorClass: 'text-orange-500',
    title: 'Visão de investimento',
    description: 'Análise de oportunidade, valorização e liquidez.',
  },
  {
    icon: Home,
    colorClass: 'text-muted-foreground',
    title: 'Curadoria sob medida',
    description: 'Você visita apenas o que realmente vale a pena.',
  },
];

export function DiferenciaisSection() {
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation();

  return (
    <section className="py-12 md:py-16 lg:py-24 bg-background">
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        {/* Centered Title */}
        <div 
          ref={titleRef}
          className={cn(
            "mb-8 md:mb-12 text-center opacity-0",
            titleVisible && "animate-fade-in-up"
          )}
          style={{ animationFillMode: 'forwards' }}
        >
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
            Por que a experiência<br />com a GC é diferente.
          </h2>
          <div className="mx-auto mt-3 md:mt-4 h-1 w-12 md:w-16 bg-primary" />
        </div>

        {/* Centered Diferenciais Grid */}
        <div ref={gridRef} className="grid gap-6 md:gap-8 grid-cols-1 sm:grid-cols-3">
          {diferenciais.map((item, index) => (
            <div 
              key={index} 
              className={cn(
                "flex flex-col items-center text-center opacity-0 py-4 sm:py-0",
                gridVisible && "animate-fade-in-up"
              )}
              style={{ 
                animationDelay: gridVisible ? `${index * 150}ms` : '0ms',
                animationFillMode: 'forwards'
              }}
            >
              <DiamondIcon icon={item.icon} colorClass={item.colorClass} />
              <h3 className="mb-2 mt-3 md:mt-4 text-lg md:text-xl font-bold text-foreground">
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground max-w-xs">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
